import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  inject,
  input,
  signal,
  type Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Money } from '@principle-theorem/accounting';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import { DepositLifecycle } from '@principle-theorem/principle-core';
import { type IAppointment } from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import { DepositHoldReleaseService } from './deposit-hold-release.service';

/**
 * The one deposit state that still speaks for itself. A hold is not money the
 * practice can spend — there is no credit behind it — so the credit lines have
 * nothing to say and this copy carries the whole story beside the Release Hold
 * control.
 *
 * Every other `DepositStatus` has lost its copy. `Required`, `Pending`,
 * `Released`, `Refunded`, `Forfeited`, `Waived` and `Cancelled` carry neither
 * credit to apply nor an action to take, so they were noise at the till. `Paid`
 * was worse than noise: a paid deposit's metadata reads `Paid` forever, so the
 * box went on announcing a deposit long after its credit had been applied. What
 * reception can do with the money is now read off the live credit totals, never
 * off deposit metadata.
 */
const cardHoldNoticeText =
  'A hold is on the patient’s card. It is not a payment and will be released automatically at checkout. Take the full invoice amount.';

/**
 * The single box the Pay step shows above its payment controls: what credit is
 * available to put against the invoice, how much of it was taken as a deposit
 * for this appointment, and the controls for both. One box rather than a stack
 * of them, so reception reads one story instead of reconciling three.
 */
@Component({
  selector: 'pr-deposit-payment-notice',
  templateUrl: './deposit-payment-notice.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DepositPaymentNoticeComponent {
  readonly depositsEnabled = toSignal(
    inject(CurrentBrandScope).depositsEnabled$,
    {
      initialValue: false,
    }
  );
  releasing = signal(false);

  holdNoticeText = cardHoldNoticeText;

  /**
   * Whether the parent has credit it can actually put against this invoice.
   * Owned by the parent because it also depends on the invoice status and on
   * automatic capture having finished, neither of which the notice can see.
   */
  creditAvailable = input(false);

  /** Loading state for the apply button. The parent owns the async work. */
  applyingCredit = input(false);

  /**
   * Remaining credit reserved for this appointment, in dollars, read live. Only
   * ever the addendum that singles out account credit from this appointment's
   * deposit within the headline total — never the headline itself. Not gated on
   * automatic capture having finished: a capture that spends only part of a
   * reserved credit leaves the rest applicable, and the box has to say so.
   */
  bookingDepositTotal = input(0);

  /**
   * Every credit the patient still has, in dollars, read live: reserved for
   * this appointment, reserved for no appointment, and reserved for another
   * appointment alike. The headline, because the box follows the patient's
   * credit and this is the same figure the Account Credit payment option
   * reports. Credit reserved elsewhere is listed unticked behind a confirmation
   * when it comes to spending it, but hiding it here left it stranded with
   * nothing on screen to reach it.
   */
  availableCreditTotal = input(0);

  /** Whether the live credit read failed, leaving the figures possibly stale. */
  creditStateQueryFailed = input(false);

  appointment = input<WithRef<IAppointment> | undefined>(undefined);

  @Output() applyCredit = new EventEmitter<void>();

  hasCreditToApply: Signal<boolean> = computed(
    () => this.availableCreditTotal() > 0
  );

  /**
   * The deposit is an addendum to the credit, never the headline, and only
   * while credit reserved for this appointment is still unspent. Gated on the
   * live reserved figure rather than the deposit's metadata status, which stays
   * `Paid` forever and so kept naming a deposit that had already been consumed.
   */
  showDepositAddendum: Signal<boolean> = computed(
    () => this.depositsEnabled() && this.bookingDepositTotal() > 0
  );

  canReleaseHold: Signal<boolean> = computed(() => {
    const appointment = this.appointment();
    return (
      this.depositsEnabled() &&
      !!appointment &&
      DepositLifecycle.canStaffReleaseHold(appointment)
    );
  });

  /** The amount being held on the card, in dollars. */
  holdAmount: Signal<number> = computed(() => {
    const metadata = this.appointment()?.deposit?.metadata;
    return metadata ? Money.fromCents(metadata.amountCents) : 0;
  });

  /**
   * Credit to use, an apply the parent is still offering, a hold to release, or
   * a warning that the figures could not be refreshed. Without one of those
   * there is nothing to act on, and a box carrying only deposit history reads at
   * the till as money that is still there.
   */
  showNotice: Signal<boolean> = computed(
    () =>
      this.hasCreditToApply() ||
      this.creditAvailable() ||
      this.canReleaseHold() ||
      this.creditStateQueryFailed()
  );

  constructor(private _holdRelease: DepositHoldReleaseService) {}

  async releaseHold(): Promise<void> {
    const appointment = this.appointment();
    if (!this.depositsEnabled() || !appointment || this.releasing()) {
      return;
    }

    this.releasing.set(true);
    try {
      await this._holdRelease.releaseHold(appointment);
    } finally {
      this.releasing.set(false);
    }
  }
}
